"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.rebuildSalesOrderItems = void 0;
const lodash_1 = require("lodash");
const SalesOrderItemBuilt_1 = require("../models/SalesOrderItemBuilt");
const SalesOrderItemFixed_1 = require("../models/SalesOrderItemFixed");
const isDeliverySalesOrderItem_1 = require("./isDeliverySalesOrderItem");
const isServiceSalesOrderItem_1 = require("./isServiceSalesOrderItem");
const TProductType_1 = require("../types/TProductType");
/**
 * Rebuild sales order items based on the latest product menu.
 *
 * Some sales order item might not be rebuilt due to:
 * - the product is no longer available in the latest menu
 * - the product is currently out of stock
 * - the product ingredients or restriction rules have changed
 *   and the newly built sales order item is no longer valid
 *
 * Any sales order item that cannot be rebuilt will be skipped.
 *
 * @param salesOrderItems sales order items to be rebuilt.
 * @param products product menu which the new sales order items are rebuilt against.
 * @param deliveryProductId the id of the delivery product from the product menu.
 * @param serviceProductId the id of the service product from the product menu.
 * @param validateModels whether to validate models (for refunds we should skip this as the product definition may have changed and
 * could cause issues)
 */
const rebuildSalesOrderItems = (salesOrderItems, products, deliveryProductId, serviceProductId, validateModels) => {
    const newSalesOrderItems = salesOrderItems
        .map((serializedSalesOrderItem) => {
        const product = products.find(product => product.productId === serializedSalesOrderItem.productId);
        // skip this sales order item if the corresponding product
        // is no longer available or out of stock
        // TODO: turn this back on when inventory is in use
        if (!product /* || product.isOutOfStock*/) {
            return null;
        }
        // rebuild using the latest stock product model
        // if it is a fixed sales order item
        if (product.type === TProductType_1.TProductType.StockedProduct) {
            const newItem = new SalesOrderItemFixed_1.SalesOrderItemFixed(product);
            newItem.salesOrderId = serializedSalesOrderItem.salesOrderId;
            newItem.quantity = serializedSalesOrderItem.quantity;
            newItem.totalPrice = serializedSalesOrderItem.totalPrice;
            newItem.priceDiscounted = serializedSalesOrderItem.priceDiscounted || 0;
            return newItem;
        }
        // rebuild using the latest product blueprint model
        // if it is a built sales order item
        // skip if not valid
        if (product.type === TProductType_1.TProductType.ProductBlueprint) {
            // need to clone the product blueprint as using it across the same
            // ordered items will use the same reference and therefore wrong
            // data like the product availability
            const productBlueprint = (0, lodash_1.cloneDeep)(product);
            const newItem = new SalesOrderItemBuilt_1.SalesOrderItemBuilt(productBlueprint);
            // rebuild full selected products
            const selectedProducts = serializedSalesOrderItem.selectedProducts.map((selectedProduct) => (Object.assign(Object.assign({}, selectedProduct), { 
                // get full product
                product: newItem.productBlueprint.findProductById(selectedProduct.product.productId) })));
            newItem.initialise(selectedProducts);
            newItem.salesOrderId = serializedSalesOrderItem.salesOrderId;
            newItem.quantity = serializedSalesOrderItem.quantity;
            newItem.totalPrice = serializedSalesOrderItem.totalPrice;
            newItem.priceDiscounted = serializedSalesOrderItem.priceDiscounted || 0;
            if (!validateModels || (validateModels && newItem.isValid)) {
                return newItem;
            }
        }
        // default to skip this sales order item if it doesn't fall into
        // any of the above categories
        return null;
    })
        .filter(item => item); // remove any null i.e. skipped sales order item
    // after rebuilding, if the delivery item is the only remaining
    // sales order item, remove it
    if (validateModels &&
        newSalesOrderItems.length === 2 &&
        (0, isDeliverySalesOrderItem_1.isDeliverySalesOrderItem)(newSalesOrderItems[1], deliveryProductId) &&
        (0, isServiceSalesOrderItem_1.isServiceSalesOrderItem)(newSalesOrderItems[0], serviceProductId)) {
        newSalesOrderItems.pop();
        newSalesOrderItems.pop();
    }
    return {
        salesOrderItems: newSalesOrderItems,
        skipped: newSalesOrderItems.length !== salesOrderItems.length
    };
};
exports.rebuildSalesOrderItems = rebuildSalesOrderItems;
//# sourceMappingURL=rebuildSalesOrderItems.js.map